/* eslint-disable complexity */
import {
  compose,
  map,
  unnest,
  take,
  partition
} from 'ramda';

import {
  unnestRemoveEmpty,
  sumPointsByGroup,
  joinWithCommas,
  propValue,
  formatYear,
  replaceCommaAmpersand,
  countTrimsPoints,
  countLocationPoints,
  withSortLocale,
  getNonEmptyUrl,
  isTaxonomyGroup,
  countNonTaxonomyPoints,
  countColorsPoints,
  preProcessing,
  sortEachByTerm,
  mapNestedTerm,
  sortAcummulatedTerms,
  withSortTermLocale,
  mapTerm
} from '../SharedRules';

import { RouteSegment } from '../RouteSegment';
import {routeGroups} from '../RouteGroups';
import { SelectedLevelTemplate, BucketType } from '../SelectedLevelTemplate';
import { metadataPrefix, metadataPostfix } from './MetadataDescription';
// Pipelines
const translateOptions = compose(unnest, mapNestedTerm, sortEachByTerm);
const formatOptions = compose(joinWithCommas, take(2), withSortLocale);
const formatFeatures = compose(joinWithCommas, mapTerm, take(2), withSortTermLocale);
const getColors = compose(joinWithCommas, mapTerm, withSortTermLocale);
const getTrims = compose(joinWithCommas, mapTerm, withSortTermLocale);

const valueTypes = {
  year: 'year',
  bodyStyles: 'bodyStyles',
  singleModel: 'singleModel',
  noModel: 'noModel',
  allModel: 'allModel',
  multiMakeModel: 'multiMakeModel',
  features: 'features',
  options: 'options',
  colors: 'colors',
  trims: 'trims',
  location: 'location'
};


const bodyStylePostRemaps = {
  truck: { value: "Trucks", bucket: "bodyStyles" },
  suv: { value: "SUVs", bucket: "bodyStyles" },
  sedan: { value: "Sedans", bucket: "bodyStyles" },
  wagon: { value: "Wagons", bucket: "bodyStyles" },
  convertible: { value: "Convertibles", bucket: "bodyStyles" },
  coupe: { value: "Coupes", bucket: "bodyStyles" },
  hatchback: { value: "Hatchbacks", bucket: "bodyStyles" },
  minivan: { value: "Minivans", bucket: "bodyStyles" }
};

const getBodyStylePostRemap = style => {
  if (!style) return style
  if (!bodyStylePostRemaps[style.toLowerCase()]) return style
  
return bodyStylePostRemaps[style.toLowerCase()];
}

const getSelectedTemplateValue = (rulesState, type) => {
   let value;

  switch(type) {
      case valueTypes.year:
          value = new SelectedLevelTemplate(BucketType.year,`${formatYear(rulesState.meta.yearsData.year, rulesState.meta.yearsData.isYearRange)}`);
          break;

      case valueTypes.singleModel:
          value = new SelectedLevelTemplate(BucketType.makeModel, `${rulesState.firstMake} ${rulesState.firstModelName}`);
          break;
      case valueTypes.allModel:
          value = new SelectedLevelTemplate(BucketType.makeModel, `${rulesState.firstMake}`);
          break;
      case valueTypes.noModel:
          value = new SelectedLevelTemplate(BucketType.makeModel, `${rulesState.firstMake}`);
          break;
      case valueTypes.multiMakeModel:
          value = new SelectedLevelTemplate(BucketType.makeModel, rulesState.firstModelName
              ? `${rulesState.firstMake} ${rulesState.firstModelName}`
              : `${rulesState.firstMake}`);
          break;
      case valueTypes.bodyStyles:
      value = new SelectedLevelTemplate(BucketType.bodyStyles, `${withSortTermLocale(rulesState.meta.bodyStylesData)[0].term}`);
          break;
      case valueTypes.features:
      value = new SelectedLevelTemplate(BucketType.features, `${rulesState.meta.featuresData}`);
          break;
      case valueTypes.options:
      value = new SelectedLevelTemplate(BucketType.options, `${formatOptions(rulesState.meta.optionsData)}`);
          break;
      case valueTypes.colors:
      value = new SelectedLevelTemplate(BucketType.colors, `${replaceCommaAmpersand(rulesState.meta.colorsData)}`);
          break;
      case valueTypes.trims:
      value = new SelectedLevelTemplate(BucketType.trims, `${rulesState.meta.trimsData}`);
          break;
      case valueTypes.location:
      value = new SelectedLevelTemplate(BucketType.location, `${rulesState.meta.locationData}`);
          break;
      default:
          break;
  }

 return value;
};

const hasMinMax = obj => obj && obj.min && obj.max;

const isValidYear = year => year
  && year.ranges
  && year.ranges.length
  && Object.keys(year.ranges[0]).length;

const hasYearRange = year => {
  const proper = isValidYear(year)
      && hasMinMax(year.ranges[0]);

  const firstYear = year.ranges[0];

  return proper
      && firstYear.min
      && firstYear.max
      && firstYear.min !== firstYear.max;
};

export const getYearsMetadataSegment = ({data:year, rulesState}) => {
  const isYearRange = hasYearRange(year);

  rulesState.meta.yearsData = {year: year.ranges[0], isYearRange};

  return RouteSegment.getSegmentWhen(
      isValidYear(year),
      ()=> getSelectedTemplateValue(rulesState, valueTypes.year),
      routeGroups.levelOne,
      isYearRange ? 2 : 1
  );
};

export const getBodyStyleMetadataSegment = ({data: {terms: bodyStyles}, rulesState}) => {
  const isValid = bodyStyles && bodyStyles.length;

  rulesState.meta.bodyStylesData = bodyStyles;

  return RouteSegment.getSegmentWhen(
      isValid,
      () => getSelectedTemplateValue(rulesState, valueTypes.bodyStyles),
      routeGroups.taxonomy
  );
};

export const getOptionsMetadataSegment = ({data: options , rulesState}) => {
  const accumulatedOptions = sortAcummulatedTerms(options);
  const res = translateOptions(accumulatedOptions[1]);

  rulesState.meta.optionsData = res;

  return RouteSegment.getSegmentWhen(
       res.length,
       ()=> getSelectedTemplateValue(rulesState, valueTypes.options),
       routeGroups.options, res.length
  );
};

export const getFeaturesMetadataSegment = ({data: {terms: features}, rulesState}) => {
  const isValid = features && features.length;
  const res = `${formatFeatures(features)}`;

  rulesState.meta.featuresData = res;

  return RouteSegment.getSegmentWhen(
      isValid,
      ()=> getSelectedTemplateValue(rulesState, valueTypes.features),
      routeGroups.features, features.length);
};

export const getColorMetadataSegment = ({data: {terms: colors}, rulesState}) => {
  const isValid = colors && colors.length;
  const orderedColors = `${getColors(colors)}`;

  rulesState.meta.colorsData = orderedColors;

  return RouteSegment.getSegmentWhen(
      isValid,
      () => getSelectedTemplateValue(rulesState, valueTypes.colors),
      routeGroups.colors, colors.length);
}

export const getTrimMetadataSegment = ({data: {terms: trims}, rulesState}) => {
  const isValid = trims && trims.length;
  const orderedTrims = `${getTrims(trims)}`

  rulesState.meta.trimsData = orderedTrims;

  return RouteSegment.getSegmentWhen(
      isValid,
      () => getSelectedTemplateValue(rulesState, valueTypes.trims),
      routeGroups.trims, trims.length);
}
export const getLocationMetadataSegment = ({data:location, rulesState}) => {
  const isValid = location && location.city;

  rulesState.meta.locationData = `${location.city}, ${location.state}`;

  return RouteSegment.getSegmentWhen(
      isValid,
      () => getSelectedTemplateValue(rulesState, valueTypes.location),
      routeGroups.location);
};

export const getMetadataSegmentForSingleModelParentMakeSelect = () => {
  return RouteSegment.getSegmentWhen(
      false,
      () => '',
      routeGroups.taxonomy);
}

export const getMetadataSegmentForSingleModelSelect = ({rulesState}) => {
  const isValid = rulesState.flags.isSingleModel;

  return RouteSegment.getSegmentWhen(
      isValid,
      () => getSelectedTemplateValue(rulesState, valueTypes.singleModel),
      routeGroups.taxonomy);
}

export const getMetadataSegmentForMultiMakeModel = ({rulesState}) => {
  const isValid = rulesState.flags.isMultiMake || rulesState.flags.isMultiModel;

  return RouteSegment.getSegmentWhen(
      isValid,
      () => getSelectedTemplateValue(rulesState, valueTypes.multiMakeModel),
      routeGroups.taxonomy);
};

export const getMetadataSegmentForAllModelSelect = ({rulesState}) => {
  const isValid = rulesState.flags.isAllModels;

  return RouteSegment.getSegmentWhen(
      isValid,
      () => getSelectedTemplateValue(rulesState, valueTypes.allModel),
      routeGroups.taxonomy);
};

export const getMetadataSegmentForNoModelSelect = ({rulesState}) => {
  const isValid = rulesState.flags.isNoModels;

  return RouteSegment.getSegmentWhen(
      isValid,
      () => getSelectedTemplateValue(rulesState, valueTypes.noModel),
      routeGroups.taxonomy);
};

// Postprocessing

const getSegmentsByBucket = segments => value => segments.find(x => x.bucket === value) || false;

const withEmptyValue = facet => `${facet && facet.value ? ` ${facet.value}` : ''}`;

const withEmpty = facet => `${facet ? ` ${facet}` : ''}`;

const getLevelOneTaxonomy = (makeModel, year, color, bodyStyles, trims, trimsValue) => (makeModel || year || color || bodyStyles|| trims)
  && `${withEmptyValue(year)}${withEmptyValue(color)}${withEmptyValue(makeModel)}${withEmptyValue(bodyStyles)}${trimsValue}`;

const getLevelThreeFilters = (features, options) => (features || options)
  && `with${withEmptyValue(features)}${options && options.value && features && features.value ? `,` : ''}${withEmptyValue(options)}`;


const getDescription = (hasSegmentsAndL1Taxo, appendCars, levelThreeFilters, modifiers) => (
  `${metadataPrefix}${hasSegmentsAndL1Taxo || ' cars'}${appendCars}${withEmpty(levelThreeFilters)}${withEmpty(modifiers)}${metadataPostfix}`
);

const getTrimsValue = (trims, makeModel) =>trims && trims.value && (makeModel && makeModel.value) ?` ${trims.value}` : '';

const getSeoComponentsData = (rulesState, segments)=> {
  const  {isSpecialPriceBand, isPriceOnly, priceBandMaxValue} = rulesState.flags;

  if(isSpecialPriceBand && isPriceOnly){
   const h1Meta =`Used Cars Under ${priceBandMaxValue / 1000}k`;
   const description = `Shop used cars under ${priceBandMaxValue / 1000}k`
    + ` for sale on Carvana. Browse used cars online & have your next vehicle delivered to your door with as soon as next day delivery`;
    
return {
          segments,
          metaDescription: description,
          seoH1: h1Meta,
          seoTitle:  `${h1Meta} For Sale Online | Carvana` 
      };

  }
  const hasSegments = segments && segments.length;
  const hasAtLeastOneSegment = hasSegments && segments.length > 0;
  const getSegments = getSegmentsByBucket(segments);

  const makeModel = getSegments('makeModel');
  const bodyStyles = getSegments('bodyStyles');
  const year = getSegments('year');
  const color = getSegments('colors'); // = rulesState.meta.color.value
  const features = getSegments('features');
  const options = getSegments('options');
  const trims = getSegments('trims')
  const location = getSegments('location');

  const trimsValue = getTrimsValue(trims, makeModel);
  const doAppendCars = !makeModel && !bodyStyles && (year || color);
  const appendCarsH1 = doAppendCars ? ' Cars':'';
  const appendCarsDesc = doAppendCars ? ' cars':'';
  const levelOneTaxonomy = getLevelOneTaxonomy(makeModel, year, color, getBodyStylePostRemap(bodyStyles.value), trims, trimsValue);

  const levelThreeFilters = getLevelThreeFilters(features, options);

  const modifiers = location && `${location.value ? `in ${location.value}` : ''}`;


  const hasSegmentsAndL1Taxo = hasSegments && levelOneTaxonomy;

  if(hasAtLeastOneSegment){
      const h1Meta = `Used${hasSegmentsAndL1Taxo || ' Cars'}${appendCarsH1}${withEmpty(levelThreeFilters)}${location.value ?
           ` for sale${withEmpty(modifiers)}` : `${withEmpty(modifiers)}`}`;
      const description = getDescription(hasSegmentsAndL1Taxo, appendCarsDesc, levelThreeFilters, modifiers);
      
      return {
          segments,
          metaDescription: description,
          seoH1: h1Meta,
          seoTitle: location.value ? `${h1Meta} | Carvana` : `${h1Meta} For Sale Online | Carvana` 
      };
  }

return null;

};

const getFuelTypeLevelOneTaxonomy = (makeModel, year, color, bodyStyles, trims, trimsValue, option) => (makeModel || year || color || bodyStyles|| trims || option || "test")
  && `${withEmptyValue(year)}${withEmptyValue(color)}${withEmptyValue(makeModel)}${withEmptyValue(option)}${withEmptyValue(bodyStyles)}${trimsValue}`;
const getFuelTypeLevelThreeFilters = features => (features)
  && `with${withEmptyValue(features)}`;
const getLevelOneTaxonomyMakeModelOptions = (year, makeModel, option) => (year || makeModel ||  option)
  && `${withEmptyValue(year)}${withEmptyValue(option)}${withEmptyValue(makeModel)}`;

const fuelTypeSeoComponentData = data => {
  if(data === null){
    return data;
  }
  const hasSegments = data.segments && data.segments.length;
  const hasTwoSegments = hasSegments && data.segments.length === 2;
  const hasThreeSegments = hasSegments && data.segments.length === 3;
  const getSegments = getSegmentsByBucket(data.segments);
  const options = getSegments('options');

  if(options){
    const simpleOptions = options.value.toLowerCase().replace(' ', '').split(',');
    const isFTO = simpleOptions.includes('electric') ||  simpleOptions.includes('gas') ||  simpleOptions.includes('hybrid');

    if(!isFTO){
      return data;
    }    
  }
  else{
    return data;
  }

  const makeModel = getSegments('makeModel');
  const bodyStyles = getSegments('bodyStyles');
  const year = getSegments('year');
  const color = getSegments('colors'); 
  const features = getSegments('features');
  const trims = getSegments('trims')
  const location = getSegments('location');
  const trimsValue = getTrimsValue(trims, makeModel);
  const doAppendCars = !makeModel && !bodyStyles;
  const appendCars = doAppendCars ? ' Cars':'';
  const showFuelTypeTwoSegments = hasTwoSegments && makeModel && options;
  const showFuelTypeThreeSegments = hasThreeSegments && makeModel && options && year;
  const showFuelTypeFirst = showFuelTypeTwoSegments || showFuelTypeThreeSegments
  const levelOneTaxonomy = showFuelTypeFirst 
    ? getLevelOneTaxonomyMakeModelOptions(year, makeModel, options)
    : getFuelTypeLevelOneTaxonomy(makeModel, year, color, getBodyStylePostRemap(bodyStyles.value), trims, trimsValue, options);
  const levelThreeFilters = getFuelTypeLevelThreeFilters(features, options);

  const modifiers = location && `${location.value ? `in ${location.value}` : ''}`;


  const hasSegmentsAndL1Taxo = hasSegments && levelOneTaxonomy;
  const h1Meta = `Used${hasSegmentsAndL1Taxo}${appendCars}${withEmpty(levelThreeFilters)}${location.value ?
      ` for sale${withEmpty(modifiers)}` : `${withEmpty(modifiers)}`}`; 
  const description = getDescription(hasSegmentsAndL1Taxo, appendCars, levelThreeFilters, modifiers);

  return {
    metaDescription: description,
    seoH1: h1Meta,
    seoTitle: location.value ? `${h1Meta} | Carvana` : `${h1Meta} For Sale Online | Carvana`
  } 
};




export const finalizeSeoData = rulesState => segments => {
  return getSeoComponentsData(rulesState,segments);
};
export const overrideFuelType = segments => {
  return fuelTypeSeoComponentData(segments);
};



const takeValues = map(propValue);

const countFeaturesPoints = sumPointsByGroup(routeGroups.features);
const countOptionsPoints = sumPointsByGroup(routeGroups.options);

const formatSegments = compose(getNonEmptyUrl, takeValues);
const withTaxonomyPartition = compose(partition(isTaxonomyGroup), unnestRemoveEmpty);
const finishFormatting = rulesState => compose(overrideFuelType, finalizeSeoData(rulesState), formatSegments, unnest);

export const postProcessingMetadata = ({results, rulesState}) => {

  rulesState.flags.nonTaxonomyPoints = countNonTaxonomyPoints(results);
  rulesState.flags.featuresPoints = countFeaturesPoints(results);
  rulesState.flags.optionsPoints = countOptionsPoints(results);
  rulesState.flags.colorsPoints = countColorsPoints(results);
  rulesState.flags.trimsPoints = countTrimsPoints(results);
  rulesState.flags.locationPoints = countLocationPoints(results);


  const parted = withTaxonomyPartition(results);

  if (rulesState.requiresCvnaid() && !rulesState.flags.isSpecialPriceBand) {
      parted[1] = [];
      rulesState.flags.hasCvnaid = true;
  }


  return {
    ...finishFormatting(rulesState)(parted),
      flags: rulesState.flags
  };
}

export const preProcessingMetadata = ({selectedFilters, rulesState}) => {
  preProcessing({selectedFilters, rulesState});
};
